import { Heart } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useToggleFavorite } from '@/api/hooks'
import type { QueueItem } from '@/audio/store'
import { Cover } from '@/components/Cover'
import { ErrorNotice } from '@/components/ui/Feedback'
import { describeError } from '@/features/search/describeError'
import { createDoubleTap } from './gestures/doubleTap'
import { useCurrentSong } from './useCurrentSong'

export function PlayerArtwork({ item, offset }: { item: QueueItem; offset: { x: number; label: string } }) {
  const current = useCurrentSong(item, true)
  const favorite = useToggleFavorite()
  const taps = useRef(createDoubleTap())
  const pending = useRef(false)
  const alive = useRef(true)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [burst, setBurst] = useState(0)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    alive.current = true
    const cancel = () => taps.current.cancel()
    window.addEventListener('blur', cancel)
    window.addEventListener('resize', cancel)
    return () => { alive.current = false; cancel(); clearTimeout(timer.current); window.removeEventListener('blur', cancel); window.removeEventListener('resize', cancel) }
  }, [])
  function celebrate() {
    if (!alive.current) return
    clearTimeout(timer.current)
    setBurst((value) => value + 1)
    timer.current = setTimeout(() => setBurst(0), 850)
  }
  async function like() {
    if (pending.current) return
    pending.current = true
    setError(null)
    try {
      const song = current.data ?? (await current.refetch()).data
      if (!song) throw new Error('Could not load song. Please try again.')
      if (!alive.current) return
      // Double-tapping is always an add action: never accidentally unlike a song.
      if (!song.is_favorite) {
        const updated = await favorite.mutateAsync({ id: item.libraryItemId, favorite: true })
        current.update(updated)
      }
      celebrate()
    } catch (failure) {
      if (alive.current) setError(describeError(failure, 'Could not add to favourites. Please try again.'))
    } finally { pending.current = false }
  }
  return <>
    <div
      data-player-cover role="button" tabIndex={0}
      aria-label={`Artwork for ${item.title}. Double-tap to add to favourites.`}
      aria-busy={favorite.isPending || undefined}
      className="relative w-full cursor-pointer touch-manipulation select-none overflow-hidden rounded-xl"
      style={{ WebkitTouchCallout: 'none' }}
      onPointerDown={(event) => taps.current.start(event, event.isPrimary, event.button)}
      onPointerMove={(event) => taps.current.move(event)}
      onPointerCancel={() => taps.current.cancel()}
      onPointerLeave={() => taps.current.cancel()}
      onPointerUp={(event) => { if (taps.current.end(event)) { event.preventDefault(); void like() } }}
      onClick={(event) => { if (event.detail === 0) void like() }}
      onKeyDown={(event) => { if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) { event.preventDefault(); void like() } }}
    >
      <div className="pointer-events-none motion-safe:transition-transform motion-safe:duration-150" style={offset.x ? { transform: `translateX(${offset.x}px)`, transition: 'none' } : undefined}>
        <Cover src={item.coverUrl} size="xl" className="shadow-2xl" />
      </div>
      {burst ? <span key={burst} aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <Heart className="player-like-heart h-28 w-28 fill-white text-white drop-shadow-lg" strokeWidth={1.5} />
      </span> : null}
      {offset.label ? <span role="status" className="pointer-events-none absolute inset-x-2 bottom-3 rounded-lg bg-surface-raised/95 p-2 text-center text-sm font-medium text-text">{offset.label}</span> : null}
      <span role="status" className="sr-only">{burst ? 'Added to favourites and library.' : ''}</span>
    </div>
    {error ? <div data-gesture-ignore><ErrorNotice onRetry={() => void like()}>{error}</ErrorNotice></div> : null}
  </>
}
